import { useTranslation } from 'react-i18next'
import { PlanName } from '../../../@types/common'
import PriceFormat from '../../Text/PriceFormater'
import { isAnnual } from '../../../services/payments/annual'

type Props = {
  className?: string
  plan: {
    name: PlanName
    price: number
    vat: number
    amount: number
    displayName: string
  }
  /** 이 구독의 결제 주기 (1=월, 12=연) */
  intervalMonth?: number | null
  /** 주기 전체 정가 (부가세 포함). 연간이면 1년치 */
  price: number
  /** 이번 회 실제 청구액 (부가세 포함). 연간 즉시 전환에서는 정가와 다르다 */
  chargeAmount: number
  /** 미도래 개월 수. 0이면 정산 줄을 그리지 않는다 */
  deductionMonths?: number
}

export default function Receipt({ className, plan, intervalMonth, price, chargeAmount, deductionMonths = 0 }: Props) {
  const { t } = useTranslation('plan-checkout')

  const annual = isAnnual(intervalMonth)
  const vatRate = plan.vat ?? 0

  // 표에는 공급가(부가세 별도)를 쓰고 합계만 부가세 포함가로 낸다 (정책 14절, D-85).
  const supply = Math.round(price / (1 + vatRate))
  const deduction = Math.round((price - chargeAmount) / (1 + vatRate))
  const vat = chargeAmount - (supply - deduction)

  return (
    <div className={className}>
      <table className="w-full [&_tr>:first-child]:pl-[32px] [&_tr>:last-child]:pr-[32px] [&_tr>:last-child]:text-right [&_tr]:pl-[30px]">
        <colgroup>
          <col className="w-[200px]" />
          <col />
          <col className="w-[100px]" />
          <col className="w-[100px]" />
        </colgroup>
        <thead className="h-[45px] rounded-[5px]">
          <tr className="[&_th]:bg-main-4 [&_th]:text-left [&_th]:text-[16px] [&_th]:font-[700]">
            <th className="rounded-l-[5px]">{t('상품')}</th>
            <th>{t('단가')}</th>
            <th>{t('수량')}</th>
            <th className="rounded-r-[5px]">{t('금액')}</th>
          </tr>
        </thead>
        <tbody className="[&_td]:text-gray-3 [&_td]:h-[40px] [&_td]:text-[14px] [&_td]:font-[400]">
          <tr>
            <td translate="no" className="">
              {plan.displayName} {annual ? t('연간 구독 플랜') : t('월간 구독 플랜')}
            </td>
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <td>
              <PriceFormat amount={supply} hideSuffix />
            </td>
            <td>1</td>
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <td>
              <PriceFormat amount={supply} hideSuffix />
            </td>
          </tr>
          {/* 미도래 달의 선결제 가치 차감. 연간 → 연간 업그레이드에서만 나온다 (정책 11.7) */}
          {deduction > 0 && deductionMonths > 0 ? (
            <tr>
              <td className="text-point-3">
                {t('남은 기간 정산 ({{months}}개월)', { months: deductionMonths })}
              </td>
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <td />
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <td />
              <td className="text-point-3">
                −<PriceFormat amount={deduction} hideSuffix />
              </td>
            </tr>
          ) : null}
          <tr>
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <td />
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <td />
            <td>{t('부가세')}(10%)</td>
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <td>
              <PriceFormat amount={vat} hideSuffix />
            </td>
          </tr>
        </tbody>
      </table>
      <div className="bg-main-4 mt-[20px] flex items-center justify-end gap-[15px] rounded-[5px] px-[20px] py-[14px] text-right">
        <span className="text-gray-2 text-[16px] font-[700]">{t('결제 금액 (부가세 포함)')}</span>
        <span className="text-main-1 text-[23px] font-[900]">
          <PriceFormat amount={chargeAmount} />
        </span>
      </div>
    </div>
  )
}
